"use client";

import Image from "next/image";
import { useState } from "react";

type BlogArtworkProps = {
  alt: string;
  priority?: boolean;
  sizes: string;
  slug: string;
  src: string | null;
  title: string;
  variant?: "card" | "hero";
};

const tones = [
  "from-slate-950 via-indigo-800 to-violet-500",
  "from-indigo-950 via-blue-700 to-cyan-500",
  "from-violet-950 via-fuchsia-700 to-rose-400",
] as const;

function getTone(slug: string): (typeof tones)[number] {
  const hash = [...slug].reduce(
    (total, character) => total + character.charCodeAt(0),
    0,
  );
  return tones[hash % tones.length];
}

export function BlogArtwork({
  alt,
  priority = false,
  sizes,
  slug,
  src,
  title,
  variant = "card",
}: BlogArtworkProps) {
  const [failed, setFailed] = useState(false);

  if (src && !failed) {
    return (
      <Image
        src={src}
        alt={alt}
        fill
        unoptimized
        priority={priority}
        sizes={sizes}
        onError={() => setFailed(true)}
        className="object-cover transition-transform duration-300 group-hover:scale-[1.02]"
      />
    );
  }

  return (
    <div
      aria-hidden="true"
      data-blog-artwork-fallback="true"
      className={`absolute inset-0 overflow-hidden bg-gradient-to-br ${getTone(slug)} text-white`}
    >
      <div className="flex h-full items-center justify-center p-6 sm:p-10">
        <p
          className={`max-w-3xl text-center font-semibold leading-tight tracking-[-0.025em] ${variant === "hero" ? "text-2xl sm:text-4xl" : "line-clamp-3 text-xl sm:text-2xl"}`}
        >
          {title}
        </p>
      </div>
    </div>
  );
}
